// articleUI.js
// Small set of presentational primitives so each article file is just content,
// not repeated inline-style boilerplate. All use the shared `colors` token and
// the site's display/body fonts, matching LandingPage.js.

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { colors } from './guidesData';

export function H2({ children }) {
  return (
    <h2
      style={{
        fontFamily: "'DM Serif Display', Georgia, serif",
        fontSize: 27,
        fontWeight: 400,
        color: colors.gray900,
        margin: '40px 0 14px',
        lineHeight: 1.2,
      }}
    >
      {children}
    </h2>
  );
}

export function H3({ children }) {
  return (
    <h3
      style={{
        fontSize: 19,
        fontWeight: 700,
        color: colors.gray800,
        margin: '28px 0 10px',
      }}
    >
      {children}
    </h3>
  );
}

export function P({ children }) {
  return (
    <p style={{ fontSize: 17, lineHeight: 1.7, color: colors.gray700, margin: '0 0 18px' }}>
      {children}
    </p>
  );
}

export function UL({ children }) {
  return (
    <ul style={{ margin: '0 0 18px', paddingLeft: 22, color: colors.gray700 }}>{children}</ul>
  );
}

export function LI({ children }) {
  return (
    <li style={{ fontSize: 17, lineHeight: 1.7, marginBottom: 8 }}>{children}</li>
  );
}

// Inline internal link to a product page. Use these liberally — internal links
// are how informational traffic flows toward conversion and pass ranking
// authority. `to` is a route path (e.g. '/register?tier=free').
export function InlineLink({ to, children }) {
  const navigate = useNavigate();
  return (
    <span
      onClick={() => navigate(to)}
      style={{
        color: colors.green700,
        fontWeight: 600,
        cursor: 'pointer',
        textDecoration: 'underline',
        textUnderlineOffset: 2,
      }}
    >
      {children}
    </span>
  );
}

// Pull-quote / callout box for emphasis.
export function Callout({ children }) {
  return (
    <div
      style={{
        background: colors.green50,
        border: `1px solid ${colors.green100}`,
        borderLeft: `4px solid ${colors.green600}`,
        borderRadius: 10,
        padding: '16px 20px',
        margin: '0 0 22px',
        fontSize: 16,
        lineHeight: 1.6,
        color: colors.gray800,
      }}
    >
      {children}
    </div>
  );
}

// End-of-article conversion block. Reused by every guide.
export function GuideCTA() {
  const navigate = useNavigate();
  return (
    <div
      style={{
        background: 'white',
        border: `1px solid ${colors.gray200}`,
        borderRadius: 14,
        padding: 28,
        margin: '40px 0 0',
        textAlign: 'center',
      }}
    >
      <h2
        style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: 24,
          fontWeight: 400,
          color: colors.gray900,
          margin: '0 0 8px',
        }}
      >
        Let the software run the busywork
      </h2>
      <p style={{ fontSize: 16, color: colors.gray600, lineHeight: 1.6, margin: '0 0 18px' }}>
        Tee Time Organizer handles the invites, RSVPs, pairings, and reminders so you can just
        play. Free for up to 8 players — no credit card required.
      </p>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
        <button
          onClick={() => navigate('/register?tier=free')}
          style={{
            background: `linear-gradient(135deg, ${colors.green700} 0%, ${colors.green900} 100%)`,
            color: 'white',
            border: 'none',
            borderRadius: 10,
            padding: '12px 22px',
            fontSize: 15,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Start free
        </button>
        <button
          onClick={() => navigate('/')}
          style={{
            background: 'white',
            color: colors.green800,
            border: `1px solid ${colors.gray300}`,
            borderRadius: 10,
            padding: '12px 22px',
            fontSize: 15,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          See how it works
        </button>
      </div>
    </div>
  );
}

// FAQ section — renders visible Q&A AND emits FAQPage JSON-LD for SEO / AI
// citation. Pass a unique `id` (so multiple pages don't collide on the script
// tag) and a `faqs` array of { q, a } objects. Questions/answers must be
// genuine — fabricated FAQ schema gets penalized.
export function FAQ({ id, faqs }) {
  useEffect(() => {
    if (!faqs || faqs.length === 0) return;
    const scriptId = `faq-jsonld-${id}`;
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = scriptId;
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
    return () => {
      // Clean up on unmount so a different page's FAQ doesn't linger.
      const existing = document.getElementById(scriptId);
      if (existing) existing.remove();
    };
  }, [id, faqs]);

  if (!faqs || faqs.length === 0) return null;

  return (
    <section style={{ margin: '40px 0 0' }}>
      <h2
        style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: 27,
          fontWeight: 400,
          color: colors.gray900,
          margin: '0 0 18px',
          lineHeight: 1.2,
        }}
      >
        Frequently asked questions
      </h2>
      {faqs.map((f, i) => (
        <div
          key={i}
          style={{
            borderTop: `1px solid ${colors.gray200}`,
            padding: '16px 0',
          }}
        >
          <h3 style={{ fontSize: 17, fontWeight: 700, color: colors.gray800, margin: '0 0 8px' }}>
            {f.q}
          </h3>
          <p style={{ fontSize: 16, lineHeight: 1.7, color: colors.gray700, margin: 0 }}>{f.a}</p>
        </div>
      ))}
    </section>
  );
}
